import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Send, PlusCircle, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, API, errMsg } from "@/lib/api";
import { STAGES, METHOD, TOPUP_STATUS, egp, fmtDate, inputCls, cardCls, btnPrimary, orderLabel } from "@/lib/labels";
import { TxList } from "./AdminPricing";
import { ConfirmDelete } from "./RequestsList";
import { PasswordInput } from "@/components/account/PasswordInput";

const AUDIT = { temp_password_set: "كلمة سر مؤقتة", reset_link_sent: "لينك إعادة تعيين" };

const CredentialsPanel = ({ c, cid, audit, onDone }) => {
  const [pw, setPw] = useState("");
  const [ask, setAsk] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (ask === "temp") { await api.post(`/admin/customers/${cid}/temp-password`, { password: pw }); setPw(""); toast.success("كلمة السر المؤقتة اتحفظت وكل جلسات العميل اتقفلت"); }
      else { await api.post(`/admin/customers/${cid}/send-reset`); toast.success("لينك إعادة التعيين اتبعت لإيميل العميل"); }
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setAsk(""); }
  };
  return (
    <div className="space-y-3 rounded-2xl bg-black/30 p-4" data-testid="admin-credentials-panel">
      <p className="font-head font-bold text-white">بيانات الدخول</p>
      <p className="text-sm text-purple-200/80">إيميل الدخول: <span dir="ltr" data-testid="admin-cred-email">{c.email}</span> · الموبايل: <span dir="ltr" data-testid="admin-cred-phone">{c.phone}</span>{c.must_change_password && <span className="mr-2 rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-200" data-testid="admin-cred-must-change">مستني يغيّر كلمة السر</span>}</p>
      <p className="text-xs text-purple-300/60">كلمات السر متخزنة مشفّرة ومينفعش تتعرض. ابعت كلمة السر المؤقتة للعميل بنفسك، وهو هيغيّرها أول ما يدخل.</p>
      <form onSubmit={(e) => { e.preventDefault(); if (pw.length >= 8) setAsk("temp"); }} className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" placeholder="كلمة سر مؤقتة (8 حروف+)" testId="admin-temp-password-input" /></div>
        <button type="submit" disabled={pw.length < 8 || busy} className="rounded-full bg-[var(--km-pink)] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" data-testid="admin-temp-password-btn">حط كلمة سر مؤقتة</button>
        <button type="button" disabled={busy || c.status === "disabled"} onClick={() => setAsk("link")} className="rounded-full bg-white/10 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50" data-testid="admin-send-reset-btn">ابعت لينك إعادة تعيين</button>
      </form>
      {audit?.length > 0 && (
        <ul className="space-y-1 text-xs text-purple-300/70" data-testid="admin-audit-list">
          {audit.map((a) => <li key={a.id}>{AUDIT[a.action] || a.action} · {a.admin_email} · {fmtDate(a.at)}</li>)}
        </ul>
      )}
      <AlertDialog open={!!ask} onOpenChange={(o) => !o && !busy && setAsk("")}>
        <AlertDialogContent dir="rtl" className="border-purple-500/30 bg-[var(--km-bg2)] text-white" data-testid="admin-cred-confirm-dialog">
          <AlertDialogHeader className="text-right sm:text-right">
            <AlertDialogTitle>{ask === "temp" ? "تحط كلمة سر مؤقتة؟" : "تبعت لينك إعادة تعيين؟"}</AlertDialogTitle>
            <AlertDialogDescription className="text-purple-200/80">{ask === "temp" ? `كل جلسات ${c.brand_name} هتتقفل ولازم يغيّر كلمة السر أول ما يدخل.` : `هيوصل لينك صالح لمدة ساعة ولمرة واحدة على ${c.email}.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={busy} data-testid="admin-cred-cancel">رجوع</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); run(); }} className="bg-[var(--km-pink)]" data-testid="admin-cred-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} تأكيد</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const AccountActions = ({ c, cid, onDone, onDeleted }) => {
  const toggle = async () => {
    try { await api.post(`/admin/customers/${cid}/status`, { disabled: c.status !== "disabled" }); toast.success(c.status === "disabled" ? "الحساب اتفعّل" : "الحساب اتقفل"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/customers/${cid}`); toast.success("الحساب اتحذف وبياناته الشخصية اتشالت"); onDeleted(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (c.status === "deleted") return <p className="rounded-xl bg-red-500/10 p-3 text-sm text-red-200" data-testid="admin-customer-deleted">الحساب محذوف (الأوردرات والمعاملات محفوظة للسجلات)</p>;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-black/30 p-3">
      <span className={`rounded-full px-3 py-1 text-xs font-bold ${c.status === "disabled" ? "bg-yellow-500/20 text-yellow-200" : "bg-green-500/20 text-green-200"}`} data-testid="admin-customer-status">{c.status === "disabled" ? "متوقف" : "شغال"}</span>
      <button onClick={toggle} className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white" data-testid="admin-customer-toggle">{c.status === "disabled" ? "فعّل الحساب" : "اقفل الحساب"}</button>
      <span className="text-xs text-purple-300/70">حذف الحساب:</span>
      <ConfirmDelete title={`تحذف حساب ${c.brand_name}؟ بياناته الشخصية هتتشال والسجلات المالية هتفضل`} onConfirm={remove} testId="admin-customer-delete" />
    </div>
  );
};

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const AdjustForm = ({ cid, balance, onDone }) => {
  const [f, setF] = useState({ kind: "credit", amount: "", reason: "" });
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const amount = parseInt(f.amount, 10) || 0;
  const after = f.kind === "debit" ? balance - amount : balance + amount;
  const invalid = amount < 1 || after < 0 || f.reason.trim().length < 3;
  const apply = async () => {
    if (busy) return;
    setBusy(true);
    try {
      onDone((await api.post(`/admin/customers/${cid}/adjust`, { ...f, amount, reason: f.reason.trim(), request_id: key.current })).data);
      key.current = newKey();
      setF({ kind: f.kind, amount: "", reason: "" });
      toast.success(f.kind === "debit" ? "الرصيد اتخصم" : "الرصيد اتضاف");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); setAsk(false); }
  };
  const set = (k) => (e) => { key.current = newKey(); setF((x) => ({ ...x, [k]: e.target.value })); };
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!invalid) setAsk(true); }} className="space-y-2 rounded-2xl bg-black/30 p-4" data-testid="admin-adjust-form">
      <p className="font-head font-bold text-white">تعديل رصيد المحفظة</p>
      <p className="text-sm text-purple-200/80">الرصيد الحالي: <b data-testid="admin-adjust-balance">{egp(balance)}</b></p>
      <div className="flex gap-2">
        {[["credit", "إضافة", PlusCircle], ["debit", "خصم", MinusCircle]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => set("kind")({ target: { value: k } })} className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-bold ${f.kind === k ? "bg-[var(--km-pink)] text-white" : "bg-white/10 text-purple-200"}`} data-testid={`admin-adjust-kind-${k}`}><Icon className="h-4 w-4" /> {l}</button>
        ))}
      </div>
      <input type="text" inputMode="numeric" dir="ltr" placeholder="المبلغ" className={`${inputCls} text-right`} value={f.amount} onChange={(e) => set("amount")({ target: { value: e.target.value.replace(/[^0-9]/g, "") } })} required data-testid="admin-adjust-amount" />
      <input placeholder="السبب (بيتسجل في السجل)" className={inputCls} value={f.reason} onChange={set("reason")} required minLength={3} maxLength={300} data-testid="admin-adjust-reason" />
      {amount > 0 && <p className={`text-sm ${after < 0 ? "text-red-300" : "text-[var(--km-cyan)]"}`} data-testid="admin-adjust-after">{after < 0 ? "الرصيد مش كفاية — مينفعش يبقى بالسالب" : `الرصيد بعد التعديل: ${egp(after)}`}</p>}
      <button type="submit" disabled={invalid || busy} className={btnPrimary} data-testid="admin-adjust-submit">مراجعة التعديل</button>
      <AlertDialog open={ask} onOpenChange={(o) => !busy && setAsk(o)}>
        <AlertDialogContent dir="rtl" className="border-purple-500/30 bg-[var(--km-bg2)] text-white" data-testid="admin-adjust-confirm-dialog">
          <AlertDialogHeader className="text-right sm:text-right">
            <AlertDialogTitle>{f.kind === "debit" ? "تأكيد خصم" : "تأكيد إضافة"} {egp(amount)}</AlertDialogTitle>
            <AlertDialogDescription className="text-purple-200/80">من {egp(balance)} إلى {egp(after)} · السبب: {f.reason}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={busy} data-testid="admin-adjust-cancel">رجوع</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); apply(); }} className="bg-[var(--km-pink)]" data-testid="admin-adjust-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} تأكيد</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};

const MessageForm = ({ cid }) => {
  const [f, setF] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/admin/customers/${cid}/message`, { title: f.title.trim(), body: f.body.trim() }); setF({ title: "", body: "" }); toast.success("الرسالة اتبعتت في الحساب وعلى الإيميل"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-2xl bg-black/30 p-4" data-testid="admin-message-form">
      <p className="font-head font-bold text-white">ابعت رسالة للعميل</p>
      <input placeholder="العنوان" className={inputCls} value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} required minLength={2} maxLength={120} data-testid="admin-message-title" />
      <textarea placeholder="الرسالة" className={`${inputCls} min-h-[80px]`} value={f.body} onChange={(e) => setF((x) => ({ ...x, body: e.target.value }))} required minLength={2} maxLength={2000} data-testid="admin-message-body" />
      <button type="submit" disabled={busy} className={btnPrimary} data-testid="admin-message-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 -scale-x-100" />} ابعت</button>
    </form>
  );
};

const CustomerDetail = ({ cid, onClose, onUpdated }) => {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/customers/${cid}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const c = d?.customer;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-h-[92vh] max-w-3xl overflow-y-auto border-purple-500/30 bg-[var(--km-bg2)] text-white" data-testid="admin-customer-detail">
        <DialogHeader className="text-right sm:text-right"><DialogTitle className="font-display text-2xl">{c?.brand_name || "بنحمّل..."}</DialogTitle></DialogHeader>
        {c && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-4">
              <span className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-white">
                {c.logo?.content_type?.startsWith("image/") && c.logo.content_type !== "image/svg+xml" ? <img src={`${API}/admin/customers/${cid}/logo`} alt="" className="h-full w-full object-contain p-1" /> : <span className="text-[10px] text-[var(--km-purple)]">{c.logo ? "ملف" : "مفيش لوجو"}</span>}
              </span>
              <div className="text-sm">
                <p dir="ltr" style={{ textAlign: "right" }}>{c.phone}</p>
                <p dir="ltr" style={{ textAlign: "right" }}>{c.email}</p>
                <p className="text-xs text-purple-300/60">اتسجل {fmtDate(c.created_at)}</p>
              </div>
              <div className="mr-auto grid grid-cols-2 gap-2 text-center text-sm">
                <span className="rounded-2xl bg-[var(--km-purple)] px-4 py-2"><b className="block text-lg" data-testid="admin-customer-balance">{egp(c.wallet_balance)}</b>الرصيد</span>
                <span className="rounded-2xl bg-black/40 px-4 py-2"><b className="block text-lg" data-testid="admin-customer-loyalty">{c.loyalty.progress}/{c.loyalty.every}</b>مكافآت متاحة: {c.loyalty.available}</span>
              </div>
            </div>
            <p className="text-xs text-purple-300/70">إعلانات مدفوعة اتسلمت: {c.loyalty.delivered_paid} · مكافآت اتكسبت: {c.loyalty.earned} · اتستخدمت: {c.loyalty.used}</p>
            <AccountActions c={c} cid={cid} onDone={() => { load(); onUpdated(); }} onDeleted={() => { onUpdated(); onClose(); }} />
            {c.status !== "deleted" && <CredentialsPanel c={c} cid={cid} audit={d.audit} onDone={load} />}
            <div className="grid gap-4 md:grid-cols-2">
              <AdjustForm cid={cid} balance={c.wallet_balance || 0} onDone={() => { load(); onUpdated(); }} />
              <MessageForm cid={cid} />
            </div>
            <section>
              <p className="font-head mb-2 font-bold">الأوردرات ({d.orders.length})</p>
              <ul className="space-y-2">{d.orders.map((o) => (
                <li key={o.id} className="flex justify-between gap-2 rounded-2xl bg-black/30 p-3 text-sm">
                  <span>{orderLabel(o)} · {o.status === "cancelled" ? "ملغي" : STAGES[o.stage]}</span><span>{o.is_free ? "مجاني" : egp(o.price)}</span>
                </li>))}</ul>
            </section>
            <section>
              <p className="font-head mb-2 font-bold">طلبات الشحن ({d.topups.length})</p>
              <ul className="space-y-2">{d.topups.map((t) => (
                <li key={t.id} className="flex justify-between gap-2 rounded-2xl bg-black/30 p-3 text-sm">
                  <span>{egp(t.amount)} · {METHOD[t.method]} · {fmtDate(t.created_at)}</span><span>{TOPUP_STATUS[t.status]}</span>
                </li>))}</ul>
            </section>
            <section><p className="font-head mb-2 font-bold">سجل المعاملات</p><TxList items={d.transactions} testId="admin-customer-tx" /></section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AdminCustomers = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/customers").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  if (items === null) return <p className="text-purple-300/70">بنحمّل...</p>;
  const s = q.trim().toLowerCase();
  const shown = items.filter((c) => !s || [c.brand_name, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(s)));
  return (
    <section data-testid="admin-customers">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-3xl text-white">العملاء <span className="text-[var(--km-pink)]">({items.length})</span></h2>
        <label className="relative w-full sm:w-72">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-300/60" />
          <input className={`${inputCls} py-2 pr-9`} placeholder="دوّر بالاسم أو الإيميل أو الرقم" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-customers-search" />
        </label>
      </div>
      {shown.length === 0 ? <p className="rounded-3xl border-2 border-dashed border-purple-500/30 p-10 text-center text-purple-300/70" data-testid="admin-customers-empty">مفيش عملاء</p> : (
        <div className="grid gap-3 md:grid-cols-2">
          {shown.map((c, i) => (
            <button key={c.id} onClick={() => setOpen(c.id)} className={`${cardCls} p-4 text-right transition-colors hover:border-[var(--km-pink)]`} data-testid={`admin-customer-${i}`}>
              <p className="font-head text-lg font-bold text-white">{c.brand_name}</p>
              {c.name && c.name !== c.brand_name && <p className="text-sm text-purple-200/80">{c.name}</p>}
              <p className="truncate text-xs text-purple-300/70" dir="ltr" style={{ textAlign: "right" }}>{c.email} · {c.phone}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className={`rounded-full px-3 py-1 font-bold ${c.status === "disabled" ? "bg-yellow-500/20 text-yellow-200" : c.status === "deleted" ? "bg-red-500/20 text-red-200" : "bg-green-500/20 text-green-200"}`} data-testid={`admin-customer-status-${i}`}>{c.status === "disabled" ? "متوقف" : c.status === "deleted" ? "محذوف" : "شغال"}</span>
                <span className="rounded-full bg-[var(--km-purple)] px-3 py-1 text-white" data-testid={`admin-customer-balance-${i}`}>{egp(c.wallet_balance)}</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-white">{c.orders_count} أوردر</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-white">مكافأة {c.loyalty.progress}/{c.loyalty.every}</span>
                {c.loyalty.available > 0 && <span className="rounded-full bg-[var(--km-cyan)] px-3 py-1 text-[#0b0414]">{c.loyalty.available} مجاني متاح</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && <CustomerDetail cid={open} onClose={() => setOpen(null)} onUpdated={load} />}
    </section>
  );
};
